import { motion } from "framer-motion";
import { Truck, Landmark, Cpu, ScrollText, ArrowRight, ShieldCheck, Snowflake, FileCheck2 } from "lucide-react";
import { Link } from "react-router-dom";
import SectionLabel from "@/components/SectionLabel";

const services = [
  {
    id: "armored-transport",
    n: "01",
    icon: Truck,
    title: "International Armored Transport",
    tagline: "Cross-border land & air convoy",
    body: "Multi-vehicle armored convoys, escort motorcades and chartered cargo aircraft moving high-value freight across sovereign borders. Every leg is planned to the second, every driver holds Interpol-verified credentials.",
    bullets: ["Level VI/VII armor plating", "Two-crew rotation with medic", "Route-of-least-visibility protocol", "Real-time C4ISR mission control"],
    img: "https://images.unsplash.com/photo-1727896908560-41f75dad9d96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxhcm1vcmVkJTIwdHJ1Y2t8ZW58MHx8fHwxNzg1MjgyMDg0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "precious-vaulting",
    n: "02",
    icon: Landmark,
    title: "Precious Cargo Vaulting",
    tagline: "High-security custodial storage",
    body: "Class III underground vaults in Zurich, Singapore, Delaware and dedicated offshore secure holds. Biometric-plus-mantrap ingress, seismic isolation, halon fire suppression and continuous armed presence at every asset tier.",
    bullets: ["24-inch hardened alloy walls", "Triple-factor biometric entry", "Insured to $2B per compartment", "Numbered account privacy"],
    img: "https://images.unsplash.com/photo-1609358905581-e5381612486e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxiYW5rJTIwdmF1bHR8ZW58MHx8fHwxNzg1MjgyMDg0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "tech-logistics",
    n: "03",
    icon: Cpu,
    title: "Tech & Electronics Logistics",
    tagline: "Climate-controlled secure supply chain",
    body: "For lithography optics, aerospace prototypes and next-generation silicon. Vibration-isolated crates, ±0.5°C climate holds, humidity below 12% RH, and full chain-of-custody audit under NDA.",
    bullets: ["ISO 14644 Class 5 handling", "Anti-static + faraday enclosure", "GPS+INS redundant tracking", "IP-protected chain of custody"],
    img: "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxjb21wdXRlciUyMHNlcnZlciUyMHJvb218ZW58MHx8fHwxNzg1MjgyMDg0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "customs-compliance",
    n: "04",
    icon: ScrollText,
    title: "Customs & Compliance Management",
    tagline: "Sovereign clearance & regulatory shielding",
    body: "In-house licensed brokers in 84 jurisdictions manage tariffs, dual-use export permits, FinCEN and OFAC filings, and diplomatic pouch arrangements — ensuring goods never dwell at a border.",
    bullets: ["Pre-cleared corridor status", "Bonded warehouse ecosystem", "Export control (ITAR/EAR) counsel", "AML / KYC certified partners"],
    img: null,
  },
];

export default function Services() {
  return (
    <div className="relative">
      <section className="relative overflow-hidden border-b border-[#A1AEC2]/10">
        <div className="absolute inset-0 aegis-grid-bg opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <SectionLabel>Doctrine of Custody</SectionLabel>
          <h1 className="mt-6 font-display font-black uppercase tracking-tight text-5xl sm:text-6xl text-[#F0F4F8] max-w-4xl leading-[0.95]">
            Four disciplines.<br /><span className="text-[#D4AF37]">One unbroken chain.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#9EABC0] leading-relaxed">
            Each service is a self-contained doctrine — armed with its own crews, vehicles, insurance and audit trail — yet
            they compose seamlessly into a single global operation.
          </p>
        </div>
      </section>

      {services.map((s, idx) => (
        <section
          key={s.id}
          id={s.id}
          className={`relative border-b border-[#A1AEC2]/10 ${idx % 2 === 1 ? "bg-[#0A1024]" : ""}`}
          data-testid={`service-${s.id}`}
        >
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-2" : ""}`}
            >
              <div className="flex items-baseline gap-4">
                <span className="font-display text-6xl lg:text-7xl font-black text-[#D4AF37]/40">{s.n}</span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{s.tagline}</div>
                  <h2 className="mt-2 font-display text-3xl lg:text-4xl font-bold text-[#F0F4F8]">{s.title}</h2>
                </div>
              </div>
              <p className="mt-8 text-[#9EABC0] leading-relaxed text-lg max-w-xl">{s.body}</p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm">
                    <ShieldCheck className="w-4 h-4 mt-0.5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-[#F0F4F8]">{b}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`service-cta-${s.id}`}
                className="mt-10 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37] border-b border-[#D4AF37] pb-1 hover:text-[#F0F4F8] hover:border-[#F0F4F8]"
              >
                Engage this service <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-1" : ""}`}
            >
              <div className="relative overflow-hidden border border-[#A1AEC2]/20 aspect-[4/3]">
                {s.img ? (
                  <>
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#060C1F]/70 via-[#060C1F]/20 to-transparent" />
                  </>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#12182B] via-[#0A1024] to-[#12182B] flex items-center justify-center relative">
                    <s.icon className="w-40 h-40 text-[#D4AF37]/30" strokeWidth={0.8} />
                    <div className="absolute inset-0 aegis-grid-bg opacity-30" />
                    <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-2">
                      {["FinCEN", "OFAC", "ITAR"].map(t => (
                        <div key={t} className="border border-[#D4AF37]/40 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">{t}</div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#060C1F]/80 border border-[#D4AF37]/40 px-3 py-1.5">
                  <s.icon className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{s.tagline}</span>
                </div>
                <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-[#D4AF37]" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-[#D4AF37]" />
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* CROSS-SERVICE STRIP */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24">
        <SectionLabel>Cross-Service Standards</SectionLabel>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, t: "TAPA Class A", d: "Every facility audited annually against the toughest facility security standard in existence." },
            { icon: Snowflake, t: "Climate Envelope", d: "±0.5°C environmental control from vault to vault — even during vehicle handoffs." },
            { icon: FileCheck2, t: "Blockchain Chain-of-Custody", d: "Every seal break signed with a cryptographic hash timeline auditable by counsel." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-[#12182B] border border-[#A1AEC2]/20 p-8 hover:border-[#D4AF37]/60">
              <Icon className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.4} />
              <h4 className="mt-6 font-display text-xl font-bold text-[#F0F4F8]">{t}</h4>
              <p className="mt-3 text-sm text-[#9EABC0] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
